import React, { useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Box,
  Chip,
  Snackbar,
  Tab,
  useMediaQuery,
  useTheme,
  Dialog,
  IconButton,
  DialogContent
} from "@material-ui/core";
import { hasRole, LifvsUser, UserStatus } from "../../models/User";
import { Lock, Report, VerifiedUser } from "@material-ui/icons";
import MaterialTable from "@material-table/core";
import Typography from "@material-ui/core/Typography";
import { Alert, TabContext, TabList, TabPanel } from "@material-ui/lab";
import { ExportCsv, ExportPdf } from "@material-table/exporters";
import { thunkFetchUsers } from "../../store/users/actions";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../store";
import { UserState } from "../../store/users/types";
import useStyles from "./styles";
import { UnwantedBehaviour } from "./UnwantedBehaviour";
import moment from "moment";
import { UserOverview } from "./UserOverview";
import CloseIcon from "@material-ui/icons/Close";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { UserContext } from "../../UserProvider";
import { useI18n } from "../../hooks/useI18n";

export interface State {
  readonly status: "NOT_LOADED" | "LOADING" | "FAILED" | "LOADED";
  readonly items: LifvsUser[];
  readonly userStatus?: UserStatus;
}

export const UserList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const styles = useStyles();
  const params = useParams();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const user = useContext(UserContext);
  const { t } = useI18n();

  const [state, setState] = useState<State>({
    status: "LOADING",
    items: []
  });

  const [showUserId, setShowUserId] = useState(params.userId || "");
  const [selectedTab, setSelectedTab] = useState("search");
  const selectTab = (event: React.ChangeEvent<{}>, newValue: string) => {
    setSelectedTab(newValue);
  };

  const usersState = useSelector<AppState, UserState>(s => s.users) || [];
  let users = usersState.users;

  if (state.userStatus) {
    users = users.filter(u => u.status == state.userStatus);
  }

  const getIconForStatus = (status: UserStatus) => {
    switch (status) {
      case UserStatus.Verified:
        return (
          <VerifiedUser
            fontSize={"small"}
            style={{ color: theme.palette.success.dark }}
          />
        );
      case UserStatus.Locked:
        return (
          <Lock
            fontSize={"small"}
            style={{ color: theme.palette.error.dark }}
          />
        );
      case UserStatus.Suspicious:
        return (
          <Report
            fontSize={"small"}
            style={{ color: theme.palette.warning.dark }}
          />
        );
      default:
        return <></>;
    }
  };

  useEffect(() => {
    dispatch(thunkFetchUsers());
  }, []);

  const renderChips = () => {
    return (
      <Box my={1} ml={1}>
        <Chip
          color={"primary"}
          icon={<Lock fontSize={"small"} />}
          label={UserStatus.Locked}
          variant={
            state.userStatus && state.userStatus === UserStatus.Locked
              ? "default"
              : "outlined"
          }
          onClick={() => {
            setState({
              ...state,
              status: "LOADING",
              items: [],
              userStatus: UserStatus.Locked
            });
          }}
          onDelete={() => {
            setState({
              ...state,
              status: "LOADING",
              items: [],
              userStatus: undefined
            });
          }}
        />
        <Chip
          color={"primary"}
          icon={<Report fontSize={"small"} />}
          label={UserStatus.Suspicious}
          variant={
            state.userStatus && state.userStatus === UserStatus.Suspicious
              ? "default"
              : "outlined"
          }
          onClick={() => {
            setState({
              ...state,
              status: "LOADING",
              items: [],
              userStatus: UserStatus.Suspicious
            });
          }}
          onDelete={() => {
            setState({
              ...state,
              status: "LOADING",
              items: [],
              userStatus: undefined
            });
          }}
        />
        <Chip
          color={"primary"}
          icon={<VerifiedUser fontSize={"small"} />}
          label={UserStatus.Verified}
          variant={
            state.userStatus && state.userStatus === UserStatus.Verified
              ? "default"
              : "outlined"
          }
          onClick={(e: any) => {
            setState({
              ...state,
              status: "LOADING",
              items: [],
              userStatus: UserStatus.Verified
            });
          }}
          onDelete={() => {
            setState({
              ...state,
              status: "LOADING",
              items: [],
              userStatus: undefined
            });
          }}
        />
      </Box>
    );
  };

  const renderUserList = () => {
    return (
      <>
        {renderChips()}
        <MaterialTable
          title=""
          isLoading={usersState.isLoading}
          columns={[
            {
              title: t("common.id"),
              field: "id",
              hidden: true,
              searchable: true
            },
            {
              title: t("common.user"),
              field: "name",
              hidden: !mobile,
              render: (user: any) => (
                <div style={{ minWidth: 180 }}>
                  <Link
                    to={"/users/" + user.id}
                    onClick={() => setShowUserId(user.id)}
                  >
                    {`${user.firstName ?? "..."} ${user.lastName ?? "..."}`}
                  </Link>
                  <Typography color="textSecondary">{user.email}</Typography>
                  {getIconForStatus(user.status)}
                </div>
              )
            },
            {
              title: t("common.name"),
              field: "name",
              export: false,
              hidden: mobile,
              customFilterAndSearch: (term, rowData) => {
                const name = `${rowData.firstName} ${rowData.lastName}`;
                return name.toLowerCase().includes(term.toLowerCase());
              },
              render: (user: any) => (
                <Link
                  to={"/users/" + user.id}
                  onClick={() => setShowUserId(user.id)}
                >
                  {`${user.firstName ?? "..."} ${user.lastName ?? "..."}`}
                </Link>
              )
            },
            {
              title: t("common.registrationDate"),
              field: "registrationDate",
              searchable: true,
              export: true,
              render: (user: any) =>
                moment(user.registrationDate).format("YYYY-MM-DD HH:mm")
            },
            {
              title: t("common.email"),
              field: "email",
              searchable: true
            },
            {
              title: t("common.address"),
              field: "address",
              export: false,
              searchable: true,
              render: (user: any) => (
                <div>
                  {user.address && user.zipCode && user.city
                    ? `${user.address}, ${user.zipCode} ${user.city}`
                    : "..."}
                </div>
              )
            },

            {
              title: t("common.phoneNumber"),
              field: "phoneNumber",
              searchable: true,
              hidden: true,
              export: true
            },
            {
              title: t("common.firstName"),
              field: "firstName",
              searchable: true,
              hidden: true,
              export: true
            },
            {
              title: t("common.lastName"),
              field: "lastName",
              searchable: true,
              hidden: true,
              export: true
            },
            {
              title: t("common.loginIdentifier"),
              field: "loginMethodIdentifier",
              searchable: true,
              hidden: true
            },
            {
              title: t("common.loginMethod"),
              field: "loginMethod.name",
              searchable: true,
              hidden: true,
              export: true
            },
            {
              title: t("common.birthDate"),
              field: "birthDate",
              searchable: true,
              hidden: true
            },
            {
              title: t("common.phoneNumber"),
              field: "phoneNumber",
              searchable: true,
              hidden: true
            },
            {
              title: t("common.zipCode"),
              field: "zipCode",
              searchable: true,
              hidden: true,
              export: true
            },
            {
              title: t("common.city"),
              field: "city",
              hidden: true,
              export: true
            },
            {
              title: t("common.address"),
              field: "address",
              hidden: true,
              export: true
            },
            {
              title: t("common.tableHeaders.status"),
              field: "status",
              hidden: mobile,
              render: (user: any) => getIconForStatus(user.status),
              export: true
            },
            {
              title: t("common.lastStatusChange"),
              field: "lastStatusChange",
              hidden: mobile,
              render: (user: any) =>
                user.lastStatusChange
                  ? moment(user.lastStatusChange).format("YYYY-MM-DD HH:mm")
                  : ""
            },
            {
              title: t("common.hasMadeAPurchase"),
              field: "hasMadeAPurchase",
              searchable: true,
              export: true,
              hidden: true,
              customExport: (user: any) =>
                user.hasMadeAPurchase ? t("common.yes") : t("common.no")
            }
          ]}
          data={users as []}
          options={{
            pageSize: 20,
            exportMenu: !mobile
              ? [
                  {
                    label: t("common.exportPdf"),
                    exportFunc: (cols, data) =>
                      ExportPdf(cols, data, t("common.users"))
                  },
                  {
                    label: t("common.exportCsv"),
                    exportFunc: (cols, data) =>
                      ExportCsv(cols, data, t("common.users"))
                  }
                ]
              : [],
            exportAllData: true
          }}
          localization={{
            header: {
              actions: ""
            }
          }}
        />

        <Dialog
          onClose={() => setShowUserId("")}
          open={showUserId !== ""}
          fullWidth={true}
          fullScreen={mobile}
          maxWidth={"xl"}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <MuiDialogTitle style={{ height: 50 }} disableTypography>
            <IconButton
              aria-label="close"
              className={styles.closeButton}
              onClick={() => setShowUserId("")}
            >
              <CloseIcon />
            </IconButton>
          </MuiDialogTitle>
          <DialogContent style={mobile ? { padding: 0 } : {}}>
            <UserOverview inputUserId={showUserId} />
          </DialogContent>
        </Dialog>

        <Snackbar open={state.status === "FAILED"}>
          <Alert
            severity={"error"}
            onClose={() => {
              setState({
                ...state,
                status: "LOADED"
              });
            }}
          >
            {t("common.failedToSearchUsers")}
          </Alert>
        </Snackbar>
      </>
    );
  };

  return hasRole(user, ["Root", "Support"]) ? (
    <>
      <TabContext value={selectedTab}>
        <div
          style={{
            boxShadow:
              "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)"
          }}
        >
          <TabList
            onChange={selectTab}
            variant={"scrollable"}
            scrollButtons={"on"}
            style={{ backgroundColor: "white" }}
          >
            <Tab label={t("common.search")} value={"search"} />
            <Tab label={t("common.userBehaviour")} value={"unwanted"} />
          </TabList>
        </div>
        <TabPanel value={"search"} id={"search"} className={styles.tabPanel}>
          {renderUserList()}
        </TabPanel>
        <TabPanel value={"unwanted"} className={styles.tabPanel}>
          <UnwantedBehaviour />
        </TabPanel>
      </TabContext>
    </>
  ) : (
    renderUserList()
  );
};
